import clsx from "clsx";
import React from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { useProductCardContext } from "./product-card";
import styles from "./product-card-details.module.css";
import ProductCardLink from "./product-card-link";

interface ProductCardDetailsProps {
    showVariant?: boolean;
    className?: string;
    feedType?: string;
    analyticsEventLabel?: string;
    hideShortDescription?: boolean | string;
}

const defaultProps = {
    showVariant: false,
};

function ProductCardDetails({
    showVariant,
    className,
    feedType,
    analyticsEventLabel,
}: ProductCardDetailsProps = defaultProps): React.ReactElement {
    const {
        designer_name: designerName,
        short_description: shortDescription,
        full_price_with_currency_symbol: fullPriceWithCurrencySymbol,
        has_variant: hasVariant,
        in_stock: inStock,
        show_retailer_as_affiliate: showRetailerAsAffiliate,
        retailer_name: retailerName,
    } = useProductCardContext();

    function handleShortDescriptionClick(): void {
        analytics.event("product_card", "short description", analyticsEventLabel);
    }

    function handleRetailerClick(): void {
        analytics.event("product_card", "retailer", analyticsEventLabel);
    }
    const productCardDescription = (
        <>
            <div className={styles.designer}>{designerName}</div>
            {!!shortDescription && (
                <div className={styles.shortDescription} onClick={handleShortDescriptionClick}>
                    {shortDescription}
                </div>
            )}
        </>
    );

    return (
        <div className={clsx(styles.details, className)}>
            <ProductCardLink reason="text" className={styles.detailsLink} feedType={feedType}>
                <h3
                    className={styles.detailsWrapper}
                    aria-description={gettext("product_card.product_description.label")}
                >
                    {productCardDescription}
                </h3>
            </ProductCardLink>
            <div className={styles.priceSection}>
                {fullPriceWithCurrencySymbol ? (
                    <ProductCardPriceSection />
                ) : (
                    <ProductCardNotAvailable />
                )}
            </div>

            {hasVariant && inStock && showVariant && (
                <p className={styles.variants}>
                    <em>{gettext("product_card.more_colors_available")}</em>
                </p>
            )}

            <div className={styles.retailer} onClick={handleRetailerClick}>
                {showRetailerAsAffiliate && <SVGIcon name="hyperlink" />}
                <span>{retailerName}</span>
            </div>
        </div>
    );
}

export function ProductCardPriceSection(): React.ReactElement {
    const {
        full_price_with_currency_symbol: fullPriceWithCurrencySymbol,
        sale_price_with_currency_symbol: salePriceWithCurrencySymbol,
        in_stock: inStock,
        free_shipping: freeShipping,
        on_promotion: onPromotion,
        sale_discount: saleDiscount,
        discount_info: discountInfo,
    } = useProductCardContext();

    if (!inStock) {
        return (
            <>
                <del className={clsx(styles.price)}>{fullPriceWithCurrencySymbol}</del>

                <span className={clsx(styles.soldOutMessage)}>
                    {gettext("product_card.sold_out")}
                </span>
            </>
        );
    }
    return (
        <>
            {freeShipping && <SVGIcon className={styles.icon} name="truck" />}

            {onPromotion && <SVGIcon className={styles.icon} name="tag" />}

            {saleDiscount ? (
                <>
                    <del className={styles.price}>{fullPriceWithCurrencySymbol}</del>
                    <span className={clsx(styles.price, styles.salePrice)}>
                        {salePriceWithCurrencySymbol}
                    </span>
                </>
            ) : (
                <span className={styles.price}>{fullPriceWithCurrencySymbol}</span>
            )}

            {discountInfo && <span className={styles.discountInfo}>{discountInfo}</span>}
        </>
    );
}

export function ProductCardNotAvailable(): React.ReactElement {
    return (
        <span className={styles.soldOutMessage}>
            {gettext("product_card.not_available_in_your_country")}
        </span>
    );
}

export default ProductCardDetails;
